/**
 * Runtime Errors: Error Normalization System
 *
 * Normalizes errors from different sources into a unified runtime error model.
 * Provides consistent error handling and user-friendly error messages.
 *
 * @platform-infrastructure
 */

import { RuntimeError } from '../contracts'

/**
 * Error Code Registry - Standardized error codes
 */
export enum ErrorCode {
  // Network errors
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  NETWORK_CONNECTION = 'NETWORK_CONNECTION',
  NETWORK_SERVER_ERROR = 'NETWORK_SERVER_ERROR',

  // Authentication errors
  AUTH_UNAUTHORIZED = 'AUTH_UNAUTHORIZED',
  AUTH_FORBIDDEN = 'AUTH_FORBIDDEN',
  AUTH_TOKEN_EXPIRED = 'AUTH_TOKEN_EXPIRED',
  AUTH_INVALID_CREDENTIALS = 'AUTH_INVALID_CREDENTIALS',

  // Validation errors
  VALIDATION_REQUIRED = 'VALIDATION_REQUIRED',
  VALIDATION_INVALID_FORMAT = 'VALIDATION_INVALID_FORMAT',
  VALIDATION_OUT_OF_RANGE = 'VALIDATION_OUT_OF_RANGE',

  // Business logic errors
  BUSINESS_RULE_VIOLATION = 'BUSINESS_RULE_VIOLATION',
  BUSINESS_INSUFFICIENT_FUNDS = 'BUSINESS_INSUFFICIENT_FUNDS',
  BUSINESS_OPERATION_NOT_ALLOWED = 'BUSINESS_OPERATION_NOT_ALLOWED',

  // Gateway errors
  GATEWAY_REQUEST_FAILED = 'GATEWAY_REQUEST_FAILED',
  GATEWAY_RESPONSE_INVALID = 'GATEWAY_RESPONSE_INVALID',
  GATEWAY_BACKEND_UNAVAILABLE = 'GATEWAY_BACKEND_UNAVAILABLE',

  // Runtime errors
  RUNTIME_EXECUTION_FAILED = 'RUNTIME_EXECUTION_FAILED',
  RUNTIME_TIMEOUT = 'RUNTIME_TIMEOUT',
  RUNTIME_UNKNOWN_ERROR = 'RUNTIME_UNKNOWN_ERROR',

  // Client errors
  CLIENT_INVALID_INPUT = 'CLIENT_INVALID_INPUT',
  CLIENT_OPERATION_CANCELLED = 'CLIENT_OPERATION_CANCELLED',
}

/**
 * Runtime Error Normalizer
 *
 * Normalizes different error types into unified RuntimeError format.
 */
export class RuntimeErrorNormalizer {
  /**
   * Normalize an error into RuntimeError format
   */
  static normalize(error: any, context?: Record<string, any>): RuntimeError {
    // Handle RuntimeError instances (already normalized)
    if (this.isRuntimeError(error)) {
      return error
    }

    // Handle Axios errors
    if (this.isAxiosError(error)) {
      return this.normalizeAxiosError(error, context)
    }

    // Handle Fetch API errors
    if (this.isFetchError(error)) {
      return this.normalizeFetchError(error, context)
    }

    // Handle JavaScript Error instances
    if (error instanceof Error) {
      return this.normalizeJavaScriptError(error, context)
    }

    // Handle string errors
    if (typeof error === 'string') {
      return this.normalizeStringError(error, context)
    }

    // Handle unknown errors
    return this.normalizeUnknownError(error, context)
  }

  /**
   * Check if error is already a RuntimeError
   */
  private static isRuntimeError(error: any): error is RuntimeError {
    return (
      error &&
      typeof error === 'object' &&
      typeof error.code === 'string' &&
      typeof error.message === 'string' &&
      typeof error.category === 'string' &&
      typeof error.severity === 'string'
    )
  }

  /**
   * Check if error is an Axios error
   */
  private static isAxiosError(error: any): boolean {
    return error && error.isAxiosError === true
  }

  /**
   * Check if error is a Fetch API error
   */
  private static isFetchError(error: any): boolean {
    return error instanceof TypeError && error.message.includes('fetch')
  }

  /**
   * Normalize Axios error
   */
  private static normalizeAxiosError(error: any, context?: Record<string, any>): RuntimeError {
    const response = error.response
    const statusCode = response?.status

    let code: ErrorCode
    let category: RuntimeError['category']
    let severity: RuntimeError['severity']
    let message: string
    let userAction: string

    if (!response) {
      // Network error
      code = ErrorCode.NETWORK_CONNECTION
      category = 'network'
      severity = 'high'
      message = 'Network connection failed'
      userAction = 'Check your internet connection and try again'
    } else if (statusCode === 401) {
      code = ErrorCode.AUTH_UNAUTHORIZED
      category = 'authentication'
      severity = 'medium'
      message = 'Authentication required'
      userAction = 'Please log in again'
    } else if (statusCode === 403) {
      code = ErrorCode.AUTH_FORBIDDEN
      category = 'authorization'
      severity = 'medium'
      message = 'Access denied'
      userAction = 'You do not have permission to perform this action'
    } else if (statusCode >= 400 && statusCode < 500) {
      code = ErrorCode.GATEWAY_REQUEST_FAILED
      category = 'client'
      severity = 'medium'
      message = response.data?.message || `Request failed (${statusCode})`
      userAction = 'Please check your input and try again'
    } else if (statusCode >= 500) {
      code = ErrorCode.NETWORK_SERVER_ERROR
      category = 'server'
      severity = 'high'
      message = 'Server error occurred'
      userAction = 'Please try again later'
    } else {
      code = ErrorCode.RUNTIME_UNKNOWN_ERROR
      category = 'unknown'
      severity = 'medium'
      message = 'An unexpected error occurred'
      userAction = 'Please try again'
    }

    return {
      code,
      message,
      category,
      severity,
      statusCode,
      originalError: error,
      context,
      retryable: this.isRetryable(statusCode),
      userAction,
    }
  }

  /**
   * Normalize Fetch API error
   */
  private static normalizeFetchError(error: any, context?: Record<string, any>): RuntimeError {
    return {
      code: ErrorCode.NETWORK_CONNECTION,
      message: 'Network request failed',
      category: 'network',
      severity: 'high',
      originalError: error,
      context,
      retryable: true,
      userAction: 'Check your internet connection and try again',
    }
  }

  /**
   * Normalize JavaScript Error
   */
  private static normalizeJavaScriptError(error: Error, context?: Record<string, any>): RuntimeError {
    // Check for specific error types
    if (error.name === 'TimeoutError') {
      return {
        code: ErrorCode.RUNTIME_TIMEOUT,
        message: 'Operation timed out',
        category: 'timeout',
        severity: 'medium',
        originalError: error,
        context,
        retryable: true,
        userAction: 'Please try again',
      }
    }

    return {
      code: ErrorCode.RUNTIME_EXECUTION_FAILED,
      message: error.message || 'An error occurred',
      category: 'client',
      severity: 'medium',
      originalError: error,
      stack: error.stack,
      context,
      retryable: false,
      userAction: 'Please try again',
    }
  }

  /**
   * Normalize string error
   */
  private static normalizeStringError(error: string, context?: Record<string, any>): RuntimeError {
    return {
      code: ErrorCode.RUNTIME_UNKNOWN_ERROR,
      message: error,
      category: 'unknown',
      severity: 'medium',
      context,
      retryable: false,
      userAction: 'Please try again',
    }
  }

  /**
   * Normalize unknown error
   */
  private static normalizeUnknownError(error: any, context?: Record<string, any>): RuntimeError {
    return {
      code: ErrorCode.RUNTIME_UNKNOWN_ERROR,
      message: 'An unexpected error occurred',
      category: 'unknown',
      severity: 'medium',
      originalError: error,
      context: {
        ...context,
        originalErrorType: typeof error,
        originalErrorString: String(error),
      },
      retryable: false,
      userAction: 'Please try again',
    }
  }

  /**
   * Check if an HTTP status code is retryable
   */
  private static isRetryable(statusCode?: number): boolean {
    if (!statusCode) return true // Network errors are usually retryable

    // 5xx errors are retryable
    if (statusCode >= 500) return true

    // 429 (Too Many Requests) is retryable
    if (statusCode === 429) return true

    // 408 (Request Timeout) is retryable
    if (statusCode === 408) return true

    // 4xx errors are generally not retryable
    return false
  }

  /**
   * Create a custom runtime error
   */
  static createError(
    code: ErrorCode,
    message: string,
    category: RuntimeError['category'] = 'unknown',
    severity: RuntimeError['severity'] = 'medium',
    context?: Record<string, any>,
    retryable: boolean = false,
    userAction?: string
  ): RuntimeError {
    return {
      code,
      message,
      category,
      severity,
      context,
      retryable,
      userAction,
    }
  }
}